import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { LabelButton, TextLink } from '@teamsnap/snap-ui';
import { APP_URL } from 'core';
import { AuthContext, User } from 'auth';
import {
  DemoType,
  TeamsnapAppConfirmationModal,
  TeamsnapAppFeatureCallout,
  useTeamsnapAppAdUpsellActions,
} from './shared';

const learnMoreLink = 'https://www.teamsnap.com/tournaments';

type RouteParams = { orgId: string };

const Tournaments: React.FC<RouteComponentProps<RouteParams>> = ({
  match: {
    params: { orgId },
  },
}) => {
  React.useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  const { user } = React.useContext(AuthContext);
  const [showModal, toggleModal] = React.useState(false);

  const [learnMore, sendEmailRequest] = useTeamsnapAppAdUpsellActions(
    user as User,
    DemoType.Tournaments,
    orgId
  );

  const isMobileDevice = window.innerWidth <= 480;

  return (
    <div id="tournaments">
      <TeamsnapAppConfirmationModal
        show={showModal}
        onClose={() => toggleModal(false)}
      >
        <h2 className="sui-heading-md sui-mb-2">Got it!</h2>
        <p>
          We will email you soon to set up your demo and answer any questions
          you might have.
        </p>
      </TeamsnapAppConfirmationModal>
      <div className="page-content sui-p-4">
        <header className="sui-flex sui-flex-col d:sui-flex-row d:sui-justify-between sui-mb-4 sui-gap-2">
          <div>
            <h2 className="sui-heading-md">TeamSnap Tournaments</h2>
            <TextLink
              href={learnMoreLink}
              onClick={(e) => {
                e.preventDefault();
                learnMore(learnMoreLink);
              }}
            >
              Learn More
            </TextLink>
          </div>
          <div>
            <LabelButton
              icon="add"
              iconPosition="left"
              labelText="Request a demo"
              sentiment="default"
              size="default"
              variantType="primary"
              onClick={() => sendEmailRequest(() => toggleModal(true))}
            />
          </div>
        </header>
        <div className="sui-flex-col d:sui-flex-row sui-flex sui-gap-3 sui-mb-4">
          <TeamsnapAppFeatureCallout>
            No-stress registration
          </TeamsnapAppFeatureCallout>
          <TeamsnapAppFeatureCallout>
            Seamless scheduling
          </TeamsnapAppFeatureCallout>
          <TeamsnapAppFeatureCallout>
            Money-making events
          </TeamsnapAppFeatureCallout>
        </div>
        <img
          alt="tournaments"
          src={`${APP_URL}/images/teamsnap-tournaments${
            isMobileDevice ? '-short' : ''
          }.png`}
        />
        <div className="sui-flex sui-flex-col d:sui-flex-row sui-gap-[64px] sui-mt-4 d:sui-mt-6 sui-items-center">
          <div className="sui-w-full d:sui-w-1/2 sui-body">
            <h1 className="sui-heading-md sui-mb-2">
              Manage tournaments like a pro with our easy&#8209;to&#8209;use
              tournaments app
            </h1>

            <p className="sui-mb-2">
              TeamSnap Tournaments allows you to easily track team
              registrations, payment methods and discounts, manage team or venue
              conflicts and blackout dates, and more.
            </p>
            <p className="sui-mb-2">
              Build a registration process that's tailor-made for your
              tournament.
            </p>

            <ul className="sui-my-2 sui-pl-4">
              <li className="sui-my-1">
                Generate easy-to-use forms with shareable links
              </li>
              <li className="sui-my-1">
                Say goodbye to manual entry and hello to teams as they
                self-register
              </li>
              <li className="sui-my-1">
                Set up payment methods and volume discounts
              </li>
            </ul>
            <p className="sui-mb-2">
              Make sure that game day is conflict-free. TeamSnap Tournaments
              helps you manage:
            </p>

            <ul className="sui-my-2 sui-pl-4">
              <li className="sui-my-1">
                Team or venue conflicts and blackout dates
              </li>
              <li className="sui-my-1">Coaches who coach multiple teams</li>
              <li className="sui-my-1">Travel times and distances</li>
            </ul>
          </div>
          <div className="sui-w-full d:sui-w-1/2 sui-relative">
            <iframe
              style={{ aspectRatio: '16 / 9' }}
              className="sui-w-full"
              allowFullScreen
              title="tournaments-registration"
              src="https://www.youtube.com/embed/d6XdVbSuAXs"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tournaments;
